<template>
  <div class="main-section help-page">
    <HeaderSite />
    <HelpCenterMenu/>
    <div class="page-main-content">
      <div v-if="showMenuTabContent === 'trainingModule'">
        <div class="page-banner bg-color-primary d-flex" id="trainingModule">
          <div class="page-banner__img-box">
            <img class="banner-img" :src="pageBanner" alt="banner"/>
          </div>
          <div class="page-banner__content d-flex w-100">
            <v-container fluid class="px-3 py-0">
              <v-row class="h-100">
                <v-col cols="8" sm="6">
                  <div class="page-banner__content--inner d-flex flex-column justify-space-between h-100">
                    <LogoBox :color-light="true"/>
                    <h1 class="page-title my-4">
                      <span class="subtitle d-block">Training Module</span>
                      OKR Feature
                      <span class="suptitle d-block">by Columbus Tech {{ currentYear }}</span>
                    </h1>
                    <p class="copyright-text">Private & Confidential &copy; Columbus Technologies {{ currentYear }}</p>
                  </div>
                </v-col>
              </v-row>
            </v-container>
          </div>
        </div>
      </div>

      <div v-if="showMenuTabContent === 'basicOfOKRs'">
        <section class="doc-intro-section doc-section bg-color-primary" id="basicOfOKRs">
          <v-container fluid>
            <LogoBox :color-light="true"/>
            <v-row class="align-center h-100">
              <v-col>
                <h2 class="doc-intro-title doc-section-title" data-count="0.1">Basics of OKRs</h2>
              </v-col>
            </v-row>
          </v-container>
        </section>
      </div>

      <div v-if="showMenuTabContent === 'strategyPyramid'">
        <section class="doc-info-section doc-section bg-color-tertiary" id="strategyPyramid">
          <v-container fluid class="position-relative">
            <v-row>
              <v-col cols="12">
                <div class="doc-info__title-box">
                  <h3 class="doc-info-title">Strategy Pyramid</h3>
                </div>
              </v-col>
              <v-col cols="12" xxl="6" md="4" class="mb-6 mb-md-0">
                <ol class="strategy-list d-flex flex-column justify-space-between pr-2">
                  <li><p class="info"><strong>The Vision</strong> statement illustrates what the organization ultimately wants to accomplish and provides a clear direction for its efforts</p></li>
                  <li><p class="info"><strong>The Mission</strong> statement articulates the organization's fundamental reason for existence, outlining its core values, activities, and the impact it seeks to make. </p></li>
                  <li><p class="info"><strong>Strategic Objectives</strong> for the year provides a clear roadmap for achieving the organization's long-term strategy and vision.</p></li>
                  <li><p class="info"><strong>Strategic Themes</strong> help in organizing and prioritizing the organization's strategic objectives, promoting alignment, focus and effective communication throughout the organization.</p></li>
                  <li><p class="info"><strong>OKRs</strong> is a structured framework that translates strategic themes and strategic objectives into actionable goals and outcomes. </p></li>
                </ol>
              </v-col>
              <v-col cols="12" md="8" class="align-self-end">
                <img class="strategy-pyramid-img" alt="strategy" :src="strategyPyramid" />
              </v-col>
            </v-row>
          </v-container>
        </section>
      </div>

      <div v-if="showMenuTabContent === 'whatAreOKRs'">
        <section class="doc-info-section doc-section bg-color-tertiary" id="whatAreOKRs">
          <v-container fluid>
            <v-row class="align-start align-md-center">
              <v-col cols="12" class="mx-auto">
                <div class="doc-info__title-box mb-8 mb-md-6">
                  <h3 class="doc-info-title">What are OKRs?</h3>
                  <p class="subtitle mt-2">OKRs, or Objectives and Key Results, are a goal-setting framework used by organisations to define and track objectives and their outcomes. This framework helps in setting clear, measurable goals and ensuring alignment across different levels of an organisation.</p>
                </div>
              </v-col>
            </v-row>
            <v-row class="info-cards justify-space-evenly">
              <v-col cols="12" sm="6">
                <div class="info-card">
                  <div class="info-card--inner">
                    <div class="card-header bg-header-primary">
                      <h5 class="card-title with-icon font-600"><img class="icon" alt="objectives" :src="objectivesIcon" />Objectives</h5>
                    </div>
                    <div class="card-body">
                      <p>These are the specific, clearly defined goals that an organization, team, or individual aims to achieve.</p>
                      <p>Objectives should be ambitious, inspiring, and qualitative.</p>
                      <p>They provide direction and set the context for what needs to be accomplished.</p>
                    </div>
                    <div class="card-footer">
                      <div class="card-footer--inner info-examples">
                        <p class="info-example"><span class="label">Example</span>
                          <span class="example-description">Deliver an exceptional customer experience to our in-store customers to ensure high customer satisfaction</span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" sm="6">
                <div class="info-card">
                  <div class="info-card--inner">
                    <div class="card-header bg-header-secondary">
                      <h5 class="card-title with-icon font-600"><img class="icon" :src="keyResultsIcon" alt="key-result" />Key Results</h5>
                    </div>
                    <div class="card-body">
                      <p>These are the specific, measurable outcomes that indicate progress toward achieving the objective.</p>
                      <p>Key Results should be quantitative and time-bound, providing a clear way to track progress and determine if the objective has been met.</p>
                    </div>
                    <div class="card-footer">
                      <div class="card-footer--inner info-examples">
                        <p><span class="label">Example 1</span><span class="example-description">Increase Net Promoter Score (NPS) to 50</span></p>
                        <p><span class="label">Example 2</span><span class="example-description">Reduce customer support response time from 8 hours to 2 hours</span></p>
                      </div>
                    </div>
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-container>
        </section>
      </div>

      <div v-if="showMenuTabContent === 'whyDoWeNeedOKRs'">
        <section class="doc-info-section doc-section bg-color-tertiary" id="whyDoWeNeedOKRs">
          <v-container fluid>
            <v-row class="align-center">
              <v-col cols="12" class="mx-auto">
                <div class="doc-info__title-box mb-8 mb-md-6">
                  <h3 class="doc-info-title">Why do we need OKRs?</h3>
                  <p class="subtitle mt-2">OKRs, or Objectives and Key Results, are essential for organisations because they provide a structured framework for setting and achieving goals.  </p>
                </div>
              </v-col>
              <v-col cols="12" class="mx-auto mb-auto">
                <ul class="info-list pl-4">
                  <li class="mb-4"><span class="label font-500">Ensure Alignment:</span> Align everyone's efforts towards common goals, creating a unified direction.</li>
                  <li class="mb-4"><span class="label font-500">Provide Clarity:</span> Define clear, specific goals and measurable outcomes, making roles and expectations transparent.</li>
                  <li class="mb-4"><span class="label font-500">Enhance Focus:</span> Help prioritize tasks and allocate resources to the most important initiatives.</li>
                  <li class="mb-4"><span class="label font-500">Promote Accountability:</span> Establish measurable key results that hold teams and individuals responsible for their progress.</li>
                  <li class="mb-4"><span class="label font-500">Boost Motivation:</span> Set ambitious objectives that inspire and engage employees, fostering a sense of purpose.</li>
                  <li class="mb-4"><span class="label font-500">Enable Agility:</span> Allow for regular reviews and adjustments, helping organisations to monitor and navigate risk proactively.</li>
                  <li class="mb-4"><span class="label font-500">Improve Performance:</span> Link strategic planning with execution, empowering teams to become more outcome centric i.e., data driven.</li>
                </ul>
              </v-col>
            </v-row>
          </v-container>
        </section>
      </div>

      <div v-if="showMenuTabContent === 'createYourOKRs'" >
        <section class="doc-intro-section doc-section bg-color-primary" id="createYourOKRs">
          <v-container fluid>
            <LogoBox :color-light="true"/>
            <v-row class="align-center h-100">
              <v-col>
                <h2 class="doc-intro-title doc-section-title" data-count="0.2">Create Your OKRs</h2>
              </v-col>
            </v-row>
          </v-container>
        </section>
      </div>

      <div v-if="showMenuTabContent === 'createAnObjective'" class="doc-section--outer">
        <section class="doc-info-section doc-section bg-color-tertiary" id="createAnObjective">
          <v-container fluid>
            <v-row>
              <v-col cols="12" class="mx-auto">
                <div class="doc-info__title-box mb-8 mb-md-6">
                  <h3 class="doc-info-title">Create an Objective</h3>
                  <p class="subtitle mt-2">Objectives in the OKR (Objectives and Key Results) framework are <span class="info-primary">clear, ambitious, and qualitative</span> statements that define what an organization, team, or individual aims to achieve within a specific timeframe.</p>
                </div>
              </v-col>
              <v-col cols="12" class="mx-auto">
                <v-row class="info-cards flex-column info-card-steps mx-0">
                  <v-col cols="12" md="4" sm="6" class="px-0">
                    <div class="info-card">
                      <div class="info-card--inner">
                        <div class="card-header">
                          <h5 class="card-title">Step 1</h5>
                        </div>
                        <div class="card-body">
                          <p>Click on Create Goals and then Create OKRs from the top navigation bar</p>
                        </div>
                      </div>
                    </div>
                  </v-col>
                  <v-col cols="12" md="4" sm="6" class="mx-auto px-0">
                    <div class="info-card info-card-center">
                      <div class="info-card--inner">
                        <div class="card-header">
                          <h5 class="card-title">Step 2</h5>
                        </div>
                        <div class="card-body">
                          <p>Complete all the mandatory fields</p>
                        </div>
                      </div>
                    </div>
                  </v-col>
                  <v-col cols="12" md="4" sm="6" class="ml-auto px-0">
                    <div class="info-card info-card-right">
                      <div class="info-card--inner">
                        <div class="card-header">
                          <h5 class="card-title">Step 3</h5>
                        </div>
                        <div class="card-body">
                          <p>Click on Save & Add Key Result (to add 1 or more Key Result for completing the OKRs) or Click on Save as Draft (to complete the OKR later)</p>
                        </div>
                      </div>
                    </div>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-container>
          <button type="button" class="scroll-down-link" @click.stop="scrollToNextBlock">
            Scroll to watch this feature video <v-icon>mdi-chevron-down</v-icon>
          </button>
        </section>
        <div class="more-info">
          <video-content :video-src="helpVideo9"></video-content>
        </div>
      </div>

      <div v-if="showMenuTabContent === 'establishRelationshipObjectives'" class="doc-section--outer">
        <section class="doc-info-section doc-section bg-color-tertiary" id="establishRelationshipObjectives">
          <v-container fluid>
            <v-row>
              <v-col cols="12" class="mx-auto">
                <div class="doc-info__title-box mb-8 mb-md-6">
                  <h3 class="doc-info-title">Establish Relationship for Objectives</h3>
                  <p class="subtitle with-icon mt-2">
                    <img class="icon" width="24" alt="bulb" :src="bulbIcon"/>
                    Cascade: An Objective-to-Objective relation that shows how Objectives are related between different organization levels. It is a  <span class="info-primary">top-down approach</span> and established between 2 levels at a time.</p>
                </div>
              </v-col>
            </v-row>
            <v-row class="info-cards justify-center">
              <v-col cols="12" sm="6">
                <div class="info-card">
                  <div class="info-card--inner">
                    <div class="card-header">
                      <h5 class="card-title">Manually adding a relationship</h5>
                    </div>
                    <div class="card-body">
                      <ol>
                        <li>Check the 'Add Relation with another Objective' checkbox</li>
                        <li>Complete Related Team and Related With fields</li>
                      </ol>
                      <p class="font-italic mt-2">Relation Type input field will be auto populated with Cascade</p>
                    </div>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" sm="6">
                <div class="info-card">
                  <div class="info-card--inner">
                    <div class="card-header">
                      <h5 class="card-title">AI Recommendation to add a relationship</h5>
                    </div>
                    <div class="card-body">
                      <ol>
                        <li>Check the 'Add Relation with another Objective' checkbox</li>
                        <li>Make sure your Objective title has been entered</li>
                        <li>Click on the AI Recommendation button</li>
                        <li>Click accept next to the recommended objective of choice</li>
                      </ol>
                    </div>
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-container>
          <button type="button" class="scroll-down-link" @click.stop="scrollToNextBlock">
            Scroll to watch this feature video <v-icon>mdi-chevron-down</v-icon>
          </button>
        </section>
        <div class="more-info">
          <video-content :video-src="helpVideo11"></video-content>
        </div>
      </div>

      <div v-if="showMenuTabContent === 'createKeyResult'" class="doc-section--outer">
        <section class="doc-info-section doc-section bg-color-tertiary" id="createKeyResult">
          <v-container fluid>
            <v-row>
              <v-col cols="12" class="mx-auto">
                <div class="doc-info__title-box mb-8 mb-md-6">
                  <h3 class="doc-info-title">Create a Key Result</h3>
                  <p class="subtitle with-icon mt-2">
                    <img class="icon" width="24" alt="bulb" :src="bulbIcon"/>
                    Key Results in the OKR (Objectives and Key Results) framework are specific, measurable outcomes that, when achieved, will indicate the successful completion of the Objective. Key Results are usually quantifiable and should have a clear target value or milestone.
                  </p>
                </div>
              </v-col>
            </v-row>
            <v-row class="info-cards justify-center">
              <v-col cols="12" md="7" sm="6">
                <div class="info-card">
                  <div class="info-card--inner">
                    <div class="card-header">
                      <h5 class="card-title">Entry Points</h5>
                    </div>
                    <div class="card-body">
                      <p class="top-intro font-weight-bold">You can only see the below options if you are the owner of the Objective or when the owner of the Objective is in your team or you have been given admin rights)</p>
                      <ol>
                        <li>Create Objective page <br>
                          Click on ”Save & Add Key Result” at the bottom of the page
                        </li>
                        <li>List View <br>
                          Click on the ”Add Key Result” under the 3 dots next to an Objective
                        </li>
                        <li>Objective Details page <br>
                          Click on ”Add Key Result” button in the Key Results section
                        </li>
                        <li>Objective Details page <br>
                          Click on “Add Key Result” under the 3 dots
                        </li>
                      </ol>
                    </div>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" md="5" sm="6">
                <div class="info-card">
                  <div class="info-card--inner">
                    <div class="card-header">
                      <h5 class="card-title">Steps for Creating a Key Result</h5>
                    </div>
                    <div class="card-body">
                      <ol>
                        <li>Land on the Create Key Result page through the entry points stated above</li>
                        <li>Complete all mandatory fields</li>
                        <li>Click on Save & Review (for publishing the OKRs) or  Click on Save as Draft (to publish OKRs later)</li>
                      </ol>
                    </div>
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-container>
          <button type="button" class="scroll-down-link" @click.stop="scrollToNextBlock">
            Scroll to watch this feature video <v-icon>mdi-chevron-down</v-icon>
          </button>
        </section>
        <div class="more-info">
          <video-content :video-src="helpVideo13"></video-content>
        </div>
      </div>

      <div v-if="showMenuTabContent === 'differentTargetTypesKeyResults'" class="doc-section--outer">
        <section  class="doc-info-section doc-section bg-color-tertiary" id="differentTargetTypesKeyResults">
          <v-container fluid>
            <v-row>
              <v-col cols="12" class="mx-auto">
                <div class="doc-info__title-box mb-8 mb-md-6">
                  <h3 class="doc-info-title">Different Target Type's for Key Result</h3>
                  <p class="subtitle mt-2">
                    <img class="icon" width="24" alt="bulb" :src="bulbIcon"/>
                    When creating a key result, you will have 9 target type options: Reach a Target, Auto-Update from Initiative Progress, Increase From, Decrease From, Stay Above, Stay Below, Maintain, Achieved or Not Achieved & Establish a Baseline
                  </p>
                </div>
              </v-col>
            </v-row>
            <v-row class="info-cards justify-center">
              <v-col cols="12" md="3" sm="6">
                <div class="info-card">
                  <div class="info-card--inner">
                    <div class="card-header">
                      <h5 class="card-title">Reach a Target</h5>
                    </div>
                    <div class="card-body">
                      <p>This Target Type sets a specific goal or metric that needs to be achieved. Once the target is reached, the key result is considered complete.</p>
                      <p class="font-italic mt-2">Example: "Reach $1 million in revenue by Q4."</p>
                    </div>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" md="3" sm="6">
                <div class="info-card">
                  <div class="info-card--inner">
                    <div class="card-header">
                      <h5 class="card-title">Auto-Update from Initiative Progress</h5>
                    </div>
                    <div class="card-body">
                      <p>This type automatically updates based on the progress of related initiatives allowing for real-time tracking of progress without manual adjustments. </p>
                      <p class="font-italic mt-2">Example: "Increase website traffic by 20% (auto-updated based on SEO campaign progress)."</p>
                    </div>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" md="3" sm="6">
                <div class="info-card">
                  <div class="info-card--inner">
                    <div class="card-header">
                      <h5 class="card-title">Increase From</h5>
                    </div>
                    <div class="card-body">
                      <p>This target requires a metric to be increased from its current baseline by a certain amount or percentage within a defined period. </p>
                      <p class="font-italic mt-2">Example: "Increase customer satisfaction score from 75% to 85%."</p>
                    </div>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" md="3" sm="6">
                <div class="info-card">
                  <div class="info-card--inner">
                    <div class="card-header">
                      <h5 class="card-title">Decrease From</h5>
                    </div>
                    <div class="card-body">
                      <p>Similar to “Increase From,” but in this case, the goal is to reduce a metric from its current level by a specified amount or percentage. </p>
                      <p class="font-italic mt-2">Example: "Decrease customer support response time from 4 hours to 2 hours."</p>
                    </div>
                  </div>
                </div>
              </v-col>
            </v-row>
            <v-row class="info-cards justify-center">
              <v-col cols="12" md="4" sm="6">
                <div class="info-card">
                  <div class="info-card--inner">
                    <div class="card-header">
                      <h5 class="card-title">Stay Above</h5>
                    </div>
                    <div class="card-body">
                      <p>This target ensures that a metric stays above a specified value throughout the defined period, requiring constant performance above a threshold. </p>
                      <p class="font-italic mt-2">Example: "Ensure employee engagement stays above 80%."</p>
                    </div>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" md="4" sm="6">
                <div class="info-card">
                  <div class="info-card--inner">
                    <div class="card-header">
                      <h5 class="card-title">Stay Below</h5>
                    </div>
                    <div class="card-body">
                      <p>The opposite of “Stay Above,” this target focuses on keeping a metric below a set threshold over a period of time.</p>
                      <p class="font-italic mt-2">Example: "Keep customer churn rate below 5%."</p>
                    </div>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" md="4" sm="6">
                <div class="info-card">
                  <div class="info-card--inner">
                    <div class="card-header">
                      <h5 class="card-title">Maintain</h5>
                    </div>
                    <div class="card-body">
                      <p>The opposite of “Stay Above,” this target focuses on keeping a metric below a set threshold over a period of time.</p>
                      <p class="font-italic mt-2">Example: "Keep customer churn rate below 5%."</p>
                    </div>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" sm="6">
                <div class="info-card">
                  <div class="info-card--inner">
                    <div class="card-header">
                      <h5 class="card-title">Achieved or Not Achieved</h5>
                    </div>
                    <div class="card-body">
                      <p>This is a binary target where the key result is either considered achieved or not achieved based on whether the set criteria are met by the end of the period.</p>
                      <p class="font-italic mt-2">Example: "Achieve 100% on-time project delivery."</p>
                    </div>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" sm="6">
                <div class="info-card">
                  <div class="info-card--inner">
                    <div class="card-header">
                      <h5 class="card-title">Establish a Baseline</h5>
                    </div>
                    <div class="card-body">
                      <p>This target is used to set an initial value for a metric that will be referenced for future improvements or adjustments.</p>
                      <p class="font-italic mt-2">Example: "Establish baseline user satisfaction score."</p>
                    </div>
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-container>
        </section>
      </div>

      <div v-if="showMenuTabContent === 'establishRelationshipKeyResults'" class="doc-section--outer">
        <section  class="doc-info-section doc-section bg-color-tertiary" id="establishRelationshipKeyResults">
          <v-container fluid>
            <v-row>
              <v-col cols="12" class="mx-auto">
                <div class="doc-info__title-box mb-8 mb-md-6">
                  <h3 class="doc-info-title">Establish Relationship for Key Result</h3>
                  <p class="subtitle mt-2">When establishing a relationship for a key result, you will have 4 options: Mirrored, Linked, Split and Dependency.</p>
                </div>
              </v-col>
            </v-row>
            <v-row class="info-cards justify-center">
              <v-col cols="12" md="3" sm="6">
                <div class="info-card relationship-card">
                  <div class="info-card--inner">
                    <div class="card-header">
                      <h5 class="card-title with-icon font-600"><img class="icon" :src="mirrorIcon" alt="mirror" />Mirrored</h5>
                    </div>
                    <div class="card-body">
                      <p>A Key Result to Key Result relation that shows how a KR is mirrored from upper to lower level and, the team that mirrors the Key Result drives and updates the Key result and the same updates automatically reflects on the Parent’s KR one level up.</p>
                    </div>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" md="3" sm="6">
                <div class="info-card relationship-card">
                  <div class="info-card--inner">
                    <div class="card-header">
                      <h5 class="card-title with-icon font-600"><img class="icon" :src="linkedIcon" alt="linked" />Linked</h5>
                    </div>
                    <div class="card-body">
                      <p>A Key Result to Key Result relation that shows how KRs are linked between different teams / departments contributing towards a common goal. Each KR will be owned and accounted by the individual team.</p>
                    </div>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" md="3" sm="6">
                <div class="info-card relationship-card">
                  <div class="info-card--inner">
                    <div class="card-header">
                      <h5 class="card-title with-icon font-600"><img class="icon" :src="splitIcon" alt="split"/>Split</h5>
                    </div>
                    <div class="card-body">
                      <p>A Key Result to Key Result relation that is a singular high-level KR being divided into several smaller KRs. These smaller KRs are distributed among different teams, each taking responsibility for a portion of delivering a bigger KR value which gets aggregated. </p>
                    </div>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" md="3" sm="6">
                <div class="info-card relationship-card">
                  <div class="info-card--inner">
                    <div class="card-header">
                      <h5 class="card-title with-icon font-600"><img class="icon" :src="dependencyIcon" alt="dependency" />Dependency</h5>
                    </div>
                    <div class="card-body">
                      <p>A Key Result to Key Result relation that shows the dependency of one Key Result's achievement on the completion or progress of another Key Result. In this relation, one KR acts as a prerequisite or enabler for the achievement of another KR.</p>
                    </div>
                  </div>
                </div>
              </v-col>
            </v-row>
            <v-row class="info-cards justify-center">
              <v-col cols="12" sm="6">
                <div class="info-card">
                  <div class="info-card--inner">
                    <div class="card-header">
                      <h5 class="card-title">Manually adding a relationship</h5>
                    </div>
                    <div class="card-body">
                      <ol>
                        <li>Check the <strong>'Add Relation with another Key Result'</strong> checkbox</li>
                        <li>Complete Relation Type field</li>
                        <li>Complete Related Team & Relation Type fields</li>
                      </ol>
                    </div>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" sm="6">
                <div class="info-card">
                  <div class="info-card--inner">
                    <div class="card-header">
                      <h5 class="card-title">Using AI Recommendation to Add a Relationship</h5>
                    </div>
                    <div class="card-body">
                      <ol>
                        <li>Check the <strong>'Add Relation with another Key Result'</strong> checkbox</li>
                        <li>Make sure your Key Result title has been entered</li>
                        <li>Click on the <strong>’AI Recommendation’</strong> button </li>
                        <li>Click <strong>’accept’</strong> next to the recommended Key Result of choice</li>
                      </ol>
                    </div>
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-container>
          <button type="button" class="scroll-down-link" @click.stop="scrollToNextBlock">
            Scroll to watch this feature video <v-icon>mdi-chevron-down</v-icon>
          </button>
        </section>
        <div class="more-info">
          <video-content :video-src="helpVideo16"></video-content>
        </div>
      </div>

      <div v-if="showMenuTabContent === 'addInitiativesUnderKeyResult'" class="doc-section--outer">
        <section class="doc-info-section doc-section bg-color-tertiary" id="addInitiativesUnderKeyResult">
          <v-container fluid>
            <v-row>
              <v-col cols="12" class="mx-auto">
                <div class="doc-info__title-box mb-8 mb-md-6">
                  <h3 class="doc-info-title">Create an Initiative</h3>
                  <p class="subtitle with-icon mt-2">
                    <img class="icon" width="24" alt="bulb" :src="bulbIcon"/>
                    Initiatives refer to a specific task or action taken by a team or individual to achieve a particular Key Result. Initiatives are the concrete steps or strategies that are planned and executed to make progress toward fulfilling an Key Result.
                  </p>
                </div>
              </v-col>
            </v-row>
            <v-row class="info-cards justify-center">
              <v-col cols="12" sm="6">
                <div class="info-card">
                  <div class="info-card--inner">
                    <div class="card-header">
                      <h5 class="card-title">Create an Initiative</h5>
                    </div>
                    <div class="card-body">
                      <ol>
                        <li>Click on <strong>Add Initiative</strong> at the bottom of the Create/Edit Key Result page</li>
                        <li>Click on <strong>Create Initiative</strong> under Create Goal Universal Button</li>
                        <li>Click on <strong>Create Initiative</strong> in the Initiatives tab on My Goals, My Team Goals or Organisation Goals pages</li>
                        <li>Click on <strong>Add Initiative</strong> in the Initiatives section of the Key Result Details page of choice</li>
                      </ol>
                    </div>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" sm="6">
                <div class="info-card">
                  <div class="info-card--inner">
                    <div class="card-header">
                      <h5 class="card-title">Steps</h5>
                    </div>
                    <div class="card-body">
                      <ol>
                        <li>Click on <strong>Create Initiative</strong> under Create Goal universal button on top right navigation on the </li>
                        <li>Complete all mandatory fields</li>
                        <li>Optionally you can Align an initiative to a key result of choice by selecting the <strong>Align Initiative to a Key Result</strong> checkbox</li>
                        <li>Click on <strong>Create</strong></li>
                      </ol>
                    </div>
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-container>
          <button type="button" class="scroll-down-link" @click.stop="scrollToNextBlock">
            Scroll to watch this feature video <v-icon>mdi-chevron-down</v-icon>
          </button>
        </section>
        <div class="more-info">
          <video-content :video-src="helpVideo18"></video-content>
        </div>
      </div>

      <div v-if="showMenuTabContent === 'reviewPublishOKRs'" class="doc-section--outer">
        <section class="doc-info-section doc-section bg-color-tertiary" id="reviewPublishOKRs">
          <v-container fluid>
            <v-row>
              <v-col cols="12" class="mx-auto">
                <div class="doc-info__title-box mb-8 mb-md-6">
                  <h3 class="doc-info-title">Review & Publish OKRs</h3>
                  <p class="subtitle with-icon mt-2">
                    <img class="icon" width="24" alt="bulb" :src="bulbIcon"/>
                    After creating an Objective and minimum 1 Key Result, you will get an opportunity to review your OKRs before publishing and making them visible organization wide.
                  </p>
                </div>
              </v-col>
            </v-row>
            <v-row class="info-cards justify-start">
              <v-col cols="12" md="10">
                <div class="info-card">
                  <div class="info-card--inner">
                    <div class="card-header">
                      <h5 class="card-title">Steps</h5>
                    </div>
                    <div class="card-body">
                      <ol>
                        <li>Once you land on the OKR Summary Page, you will see a <strong>‘pencil icon’</strong> appearing under Objective and each Key Resul</li>
                        <li>Click on the <strong>‘pencil icon’</strong> that you wish to apply changes to</li>
                        <li>Update the required mandatory fields on the open form</li>
                        <li>Click on <strong>Save</strong> and you will return to the OKR Summary page</li>
                        <li>Review the changes applied and then click on <strong>Save & Publish</strong> (to publish the OKRs immediately) or Click on <strong> Save as Draft</strong> (to publish your OKRs later)</li>
                      </ol>
                    </div>
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-container>
          <button type="button" class="scroll-down-link" @click.stop="scrollToNextBlock">
            Scroll to watch this feature video <v-icon>mdi-chevron-down</v-icon>
          </button>
        </section>
        <div class="more-info">
          <video-content :video-src="helpVideo20"></video-content>
        </div>
      </div>

      <div v-if="showMenuTabContent === 'independent-initiatives'" class="doc-section--outer">
        <section class="doc-info-section doc-section bg-color-tertiary" id="independent-initiatives">
          <v-container fluid>
            <v-row>
              <v-col cols="12" class="mx-auto">
                <div class="doc-info__title-box mb-8 mb-md-6">
                  <h3 class="doc-info-title">Independent Initiatives</h3>

                </div>
              </v-col>
            </v-row>
            <v-row class="info-cards">
              <v-col cols="12" md="8" sm="10">
                <div class="info-card">
                  <div class="info-card--inner">

                  </div>
                </div>
              </v-col>
            </v-row>
          </v-container>
          <button type="button" class="scroll-down-link" @click.stop="scrollToNextBlock">
            Scroll to watch this feature video <v-icon>mdi-chevron-down</v-icon>
          </button>
        </section>
        <div class="more-info">
          <video-content :video-src="helpVideo20"></video-content>
        </div>
      </div>

      <div v-if="showMenuTabContent === 'manageYourOKRs'">
        <section class="doc-intro-section doc-section bg-color-primary" id="manageYourOKRs">
          <v-container fluid>
            <LogoBox :color-light="true"/>
            <v-row class="align-center h-100">
              <v-col>
                <h2 class="doc-intro-title doc-section-title" data-count="0.3">Manage Your OKRs</h2>
              </v-col>
            </v-row>
          </v-container>
        </section>
      </div>

      <div v-if="showMenuTabContent === 'updateKeyResults'" class="doc-section--outer">
        <section class="doc-info-section doc-section bg-color-tertiary" id="updateKeyResults">
          <v-container fluid>
            <v-row>
              <v-col cols="12" class="mx-auto">
                <div class="doc-info__title-box mb-8 mb-md-6">
                  <h3 class="doc-info-title">Update Key Results</h3>
                  <p class="subtitle with-icon mt-2">
                    <img class="icon" width="24" alt="bulb" :src="bulbIcon"/>
                    Key Results will be updated according to the new total value input. E.g. If your previous value was 5% and your new progress status is 8%, you should enter 8% as the updated total value to accurately reflect the progress. You can update a Key Result for a past date within the OKR cycle by using the date option in the top right corner.                  </p>
                </div>
              </v-col>
            </v-row>
            <v-row class="info-cards">
              <v-col cols="12" md="6">
                <div class="info-card">
                  <div class="info-card--inner">
                    <div class="card-header">
                      <h5 class="card-title">Entry Points</h5>
                    </div>
                    <div class="card-body">
                      <p class="top-intro">You can only see the below option if you are the owner of the Key Result or your team member is the owner of the Key Result or  have been given admin rights.</p>
                      <ol>
                        <li>List View: Click on Update under Actions column next to a Key Result you or a team member owns</li>
                        <li>List view: Click on Update under the 3 dots you own</li>
                        <li>Key Results Details page: Click on Update button on top right side of page</li>
                        <li>Key Results Details page: Click on Update under the 3 dots </li>
                        <li>Objective Details page: Scroll down to the Key Results section and click on Update under Actions column next to a Key Result you own</li>
                        <li>Objective Details page: Scroll down to the Key Results section and click on Update under the 3 dots next to a Key Result you own</li>
                      </ol>
                    </div>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <ul class="update-key-steps">
                  <li class="update-key-step mr-16">
                    <h6 class="title">Step 1</h6>
                    <div class="desc">
                      <p>Land on the <strong>Update Key Result</strong> page through the entry points stated above</p>
                    </div>
                  </li>
                  <li class="update-key-step mr-12">
                    <h6 class="title">Step 2</h6>
                    <div class="desc">
                      <p>Fill in New Value on the New Value Total field</p>
                    </div>
                  </li>
                  <li class="update-key-step selected mr-8">
                    <h6 class="title">Step 3</h6>
                    <div class="desc">
                      <p>Use the Back Date field to change the date of the update</p>
                    </div>
                  </li>
                  <li class="update-key-step mr-4">
                    <h6 class="title">Step 4</h6>
                    <div class="desc">
                      <p><strong>Optionally choose</strong> your confidence level
                        (Confidence level indicates the certainty that reported progress accurately reflects Key Result achievement status.)
                      </p>
                    </div>
                  </li>
                  <li class="update-key-step">
                    <h6 class="title">Step 5</h6>
                    <div class="desc">
                      <p>Optionally you can add a comment below</p>
                    </div>
                  </li>
                </ul>
              </v-col>
            </v-row>
          </v-container>
          <button type="button" class="scroll-down-link" @click.stop="scrollToNextBlock">
            Scroll to watch this feature video <v-icon>mdi-chevron-down</v-icon>
          </button>
        </section>
        <div class="more-info">
          <video-content :video-src="helpVideo23"></video-content>
        </div>
      </div>

      <div v-if="showMenuTabContent === 'manageKeyResults'" class="doc-section--outer">
        <section class="doc-info-section doc-section bg-color-tertiary" id="manageKeyResults">
          <v-container fluid>
            <v-row>
              <v-col cols="12" class="mx-auto">
                <div class="doc-info__title-box mb-8 mb-md-6">
                  <h3 class="doc-info-title">Manage Key Result Weights</h3>
                  <p class="subtitle with-icon mt-2">
                    <img class="icon" width="24" alt="bulb" :src="bulbIcon"/>
                    In OKRs, an objective is typically achieved through the completion of key results. These key results are assigned weightings that determine their contribution to the overall objective. For example: <br>
                    An objective may have several key results, each contributing a certain percentage toward achieving the objective.<br>
                    The weight of each key result should sum up to 100% for a given objective. <br>
                    <span class="info-primary">So adjusting these weights allows teams to prioritise key results that are more ciritcal to achieving the objective.</span>
                  </p>
                </div>
              </v-col>
            </v-row>
            <v-row class="info-cards">
              <v-col cols="12" md="6">
                <div class="info-card">
                  <div class="info-card--inner">
                    <div class="card-header">
                      <h5 class="card-title">Entry Points</h5>
                    </div>
                    <div class="card-body">
                      <p class="top-intro">You can only see the below option if you are the owner of the Key Result or your team member is the owner of the Key Result or have been given admin rights</p>
                      <ol>
                        <li>List view: Click on Manage KR Weight, under the 3 dots you or a team member owns</li>
                        <li>Key Results Details page: Click on Manage KR Weight under the 3 dots</li>
                        <li>Objective Details page:  Scroll down to the Key Results section and click on Manage KR Weight under the 3 dots next to a Key Result you or your team member owns</li>
                      </ol>
                    </div>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <ul class="update-key-steps">
                  <li class="update-key-step mr-16">
                    <h6 class="title">Step 1</h6>
                    <div class="desc">
                      <p>Land on the <strong>Manage Key Result Weights</strong> page through the entry points stated above</p>
                    </div>
                  </li>
                  <li class="update-key-step mr-12">
                    <h6 class="title">Step 2</h6>
                    <div class="desc">
                      <p>Change Key Result Weightings making sure all key results weight percentage sums up to 100%</p>
                    </div>
                  </li>
                  <li class="update-key-step selected mr-8">
                    <h6 class="title">Step 3</h6>
                    <div class="desc">
                      <p>Click on <strong>Save Changes</strong></p>
                    </div>
                  </li>
                </ul>
              </v-col>
            </v-row>
          </v-container>
        </section>
      </div>

      <div v-if="showMenuTabContent === 'viewMyGoals'" class="doc-section--outer">
        <section class="doc-info-section doc-section bg-color-tertiary" id="viewMyGoals">
          <v-container fluid>
            <v-row>
              <v-col cols="12" class="mx-auto">
                <div class="doc-info__title-box mb-8 mb-md-6">
                  <h3 class="doc-info-title">View My Goals</h3>
                  <p class="subtitle with-icon mt-2">
                    <img class="icon" width="24" alt="bulb" :src="bulbIcon"/>
                    My Goals list view allows you to see all the key results you own,even if you do not own the corresponding objective. You also have the option to toggle between OKRs view and Initiatives view. This is also the only page where you will be able to see your saved
                    <span class="info-primary">OKR drafts / unpublished OKRs.</span>
                  </p>
                </div>
              </v-col>
            </v-row>
            <v-row class="info-cards">
              <v-col cols="12" md="4" sm="6">
                <div class="info-card">
                  <div class="info-card--inner">
                    <div class="card-header">
                      <h5 class="card-title">Entry Point to My Goals Tab</h5>
                    </div>
                    <div class="card-body">
                      <ol>
                        <li>Click on My Goals on the side navigation</li>
                      </ol>
                    </div>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" md="4" sm="6">
                <div class="info-card">
                  <div class="info-card--inner">
                    <div class="card-header">
                      <h5 class="card-title">Actions (under the 3 dots) on My Goals tab</h5>
                    </div>
                    <div class="card-body">
                      <ol>
                        <li>Objectives you or a team member owns – Add Key Result, Edit, Manage KR Weight, On Hold & Close</li>
                        <li>Key Result's you own – Update, Add Initiative & Edit</li>
                      </ol>
                    </div>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" md="4" sm="6">
                <div class="info-card">
                  <div class="info-card--inner">
                    <div class="card-header">
                      <h5 class="card-title">Primary Actions on My Goals tab</h5>
                    </div>
                    <div class="card-body">
                      <ol>
                        <li>Other's Objectives – Comment</li>
                        <li>Key Result's you own – Update</li>
                      </ol>
                    </div>
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-container>
          <button type="button" class="scroll-down-link" @click.stop="scrollToNextBlock">
            Scroll to watch this feature video <v-icon>mdi-chevron-down</v-icon>
          </button>
        </section>
        <div class="more-info">
          <video-content :video-src="helpVideo25"></video-content>
        </div>
      </div>

      <div v-if="showMenuTabContent === 'viewMyTeamGoals'" class="doc-section--outer">
        <section class="doc-info-section doc-section bg-color-tertiary" id="viewMyTeamGoals">
          <v-container fluid>
            <v-row>
              <v-col cols="12" class="mx-auto">
                <div class="doc-info__title-box mb-8 mb-md-6">
                  <h3 class="doc-info-title">View My Team Goals</h3>
                  <p class="subtitle with-icon mt-2">
                    <img class="icon" width="24" alt="bulb" :src="bulbIcon"/>
                    My Team Goals list view allows you to see the full set of OKRs of the teams that you are part of as a manager or as a team member. If you are in multiple teams, you will have the option of toggling your view to view one team's OKR sets at a time. You also have the option to toggle between OKRs view and Initiatives view
                  </p>
                </div>
              </v-col>
            </v-row>
            <v-row class="info-cards justify-center">
              <v-col cols="12" md="4" sm="6">
                <div class="info-card">
                  <div class="info-card--inner">
                    <div class="card-header">
                      <h5 class="card-title">Entry Point to My Team Goals Tab</h5>
                    </div>
                    <div class="card-body">
                      <ol>
                        <li>Click on My Team Goals on side navigation</li>
                      </ol>
                    </div>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" md="4" sm="6">
                <div class="info-card">
                  <div class="info-card--inner">
                    <div class="card-header">
                      <h5 class="card-title">Actions (under the 3 dots) on My Team Goals tab</h5>
                    </div>
                    <div class="card-body">
                      <ol>
                        <li>Objectives you or a team member owns – Add Key Result, Edit and Close</li>
                        <li>Key Result's you or your team member owns – Update, Add Initiative and Edit</li>
                      </ol>
                    </div>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" md="4" sm="6">
                <div class="info-card">
                  <div class="info-card--inner">
                    <div class="card-header">
                      <h5 class="card-title">Primary Actions (appearing next to OKRs) on My Team Goals tab</h5>
                    </div>
                    <div class="card-body">
                      <ol>
                        <li>Key Result's you or your team member owns – Update</li>
                      </ol>
                    </div>
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-container>
          <button type="button" class="scroll-down-link" @click.stop="scrollToNextBlock">
            Scroll to watch this feature video <v-icon>mdi-chevron-down</v-icon>
          </button>
        </section>
        <div class="more-info">
          <video-content :video-src="helpVideo27"></video-content>
        </div>
      </div>

      <div v-if="showMenuTabContent === 'viewOrganisationGoals'" class="doc-section--outer">
        <section class="doc-info-section doc-section bg-color-tertiary" id="viewOrganisationGoals">
          <v-container fluid>
            <v-row>
              <v-col cols="12" class="mx-auto">
                <div class="doc-info__title-box mb-8 mb-md-6">
                  <h3 class="doc-info-title">View My Organisation Goals</h3>
                  <p class="subtitle with-icon mt-2">
                    <img class="icon" width="24" alt="bulb" :src="bulbIcon"/>
                    Organisation Goals list view allows you to see all published OKR sets in your organisation.<br>
                    The OKRs are split into different levels: Organisation, Department, Sub-Department and Team, depending on how your organisation is set up.<br>
                    You also have the option to toggle between OKRs view and Initiatives view
                  </p>
                </div>
              </v-col>
            </v-row>
            <v-row class="info-cards justify-center">
              <v-col cols="12" md="4" sm="6">
                <div class="info-card">
                  <div class="info-card--inner">
                    <div class="card-header">
                      <h5 class="card-title">Entry Point to Organisation Goals Tab</h5>
                    </div>
                    <div class="card-body">
                      <ol>
                        <li>Click on OKRs on the side navigation</li>
                        <li>Click on Organisation OKRs tab</li>
                      </ol>
                    </div>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" md="4" sm="6">
                <div class="info-card">
                  <div class="info-card--inner">
                    <div class="card-header">
                      <h5 class="card-title">Actions (under the 3 dots) on Organisation Goals tab</h5>
                    </div>
                    <div class="card-body">
                      <ol>
                        <li>Objectives you or a team member owns – Add Key Result, Edit, Manage KR Weight, On Hold and Close</li>
                        <li>Other's Objectives – View Details, Comment</li>
                        <li>Key Result's you or a team member owns – Update, Add Initiative and Edit
                          Other's Key Results – View Details, Comment</li>
                      </ol>
                    </div>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" md="4" sm="6">
                <div class="info-card">
                  <div class="info-card--inner">
                    <div class="card-header">
                      <h5 class="card-title">Actions (under the Actions column) on Organisation Goals</h5>
                    </div>
                    <div class="card-body">
                      <ol>
                        <li>Other's Objectives – Comment</li>
                        <li>Key Result's you or a team member owns – Update</li>
                        <li>Other's Key Results – Comment</li>
                      </ol>
                    </div>
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-container>
          <button type="button" class="scroll-down-link" @click.stop="scrollToNextBlock">
            Scroll to watch this feature video <v-icon>mdi-chevron-down</v-icon>
          </button>
        </section>
        <div class="more-info">
          <video-content :video-src="helpVideo29"></video-content>
        </div>
      </div>

      <div v-if="showMenuTabContent === 'alignmentView'" class="doc-section--outer">
        <section class="doc-info-section doc-section bg-color-tertiary" id="alignmentView">
          <v-container fluid>
            <v-row>
              <v-col cols="12" class="mx-auto">
                <div class="doc-info__title-box mb-8 mb-md-6">
                  <h3 class="doc-info-title">Alignment Across All OKRs</h3>
                  <p class="subtitle with-icon mt-2">
                    <img class="icon" width="24" alt="bulb" :src="bulbIcon"/>
                    An OKR Alignment page helps ensure that goals at all levels (individual, team, department, and organisational) are aligned and support each other. It provides a clear view of how individual and team objectives contribute to broader organisational goals.
                  </p>
                </div>
              </v-col>
            </v-row>
            <v-row class="info-cards justify-center">
              <v-col cols="12" sm="6">
                <div class="info-card">
                  <div class="info-card--inner">
                    <div class="card-header">
                      <h5 class="card-title">Entry Point to Alignment Page</h5>
                    </div>
                    <div class="card-body">
                      <ol>
                        <li>Click on OKRs on the side navigation</li>
                        <li>Click on Alignment View on the side navigation</li>
                      </ol>
                    </div>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" sm="6">
                <div class="info-card">
                  <div class="info-card--inner">
                    <div class="card-header">
                      <h5 class="card-title">How to navigate</h5>
                    </div>
                    <div class="card-body">
                      <ul>
                        <li>Drag the screen with your mouse to navigate through the screen</li>
                        <li>My Team OKRs button to highlight OKR sets for your team</li>
                        <li>Click on Objective Display Cards to see full OKR set</li>
                        <li>Relationship clickable icon  available to click to see which OKRs are related with other OKRs</li>
                        <li>Full Screen Mode icon</li>
                        <li>Zoom In/Out feature</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-container>
          <button type="button" class="scroll-down-link" @click.stop="scrollToNextBlock">
            Scroll to watch this feature video <v-icon>mdi-chevron-down</v-icon>
          </button>
        </section>
        <div class="more-info">
          <video-content :video-src="helpVideo31"></video-content>
        </div>
      </div>

      <div v-if="showMenuTabContent === 'dashboards'" class="doc-section--outer">
        <section class="doc-info-section doc-section bg-color-tertiary" id="dashboards">
          <v-container fluid>
            <v-row>
              <v-col cols="12" class="mx-auto">
                <div class="doc-info__title-box mb-8 mb-md-6">
                  <h3 class="doc-info-title">Dashboards</h3>
                  <p class="subtitle with-icon mt-2">
                    <img class="icon" width="24" alt="bulb" :src="bulbIcon"/>
                    Dashboards provide a central view of OKR progress and metrics. The Organisation Dashboard displays company-wide goals and initiatives, while Dynamic Dashboards let you create and customise private or team views with tailored widgets.
                  </p>
                </div>
              </v-col>
            </v-row>
            <v-row class="info-cards">
              <v-col cols="12">
                <div class="info-card">
                  <div class="info-card--inner">
                    <div class="card-header">
                      <h5 class="card-title">Entry Point to Dashboards</h5>
                    </div>
                    <div class="card-body">
                      <p>1. Click on Dashboards on the side navigation</p>
                    </div>
                  </div>
                </div>
              </v-col>
            </v-row>
            <v-row class="info-cards justify-center mt-6">
              <v-col cols="12" sm="6">
                <div class="info-card">
                  <div class="info-card--inner">
                    <div class="card-header">
                      <h5 class="card-title">Dynamic Dashboards</h5>
                    </div>
                    <div class="card-body">
                      <ol>
                        <li>Private – Personal view with your chosen widgets.</li>
                        <li>Team – Shared view dashboard</li>
                        <li>Feature: Add Widgets (Key Notes, Card Visuals, OKRs List, Initiatives List & Scorecard</li>
                      </ol>
                    </div>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" sm="6">
                <div class="info-card">
                  <div class="info-card--inner">
                    <div class="card-header">
                      <h5 class="card-title">Organisation Dashboard</h5>
                    </div>
                    <div class="card-body">
                      <ol>
                        <li>View company-wide OKRs & Initiatives</li>
                        <li>Real-time updates, visible to all</li>
                      </ol>
                    </div>
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-container>
          <button type="button" class="scroll-down-link" @click.stop="scrollToNextBlock">
            Scroll to watch this feature video <v-icon>mdi-chevron-down</v-icon>
          </button>
        </section>
        <div class="more-info">
          <video-content :video-src="helpVideo35"></video-content>
        </div>
      </div>

      <div v-if="showMenuTabContent === 'calculationsGuide'" class="doc-section--outer">
        <section class="doc-info-section doc-section bg-color-tertiary" id="calculationsGuide">
          <v-container fluid>
            <v-row>
              <v-col cols="12" class="mx-auto">
                <div class="doc-info__title-box mb-6 mb-md-8">
                  <h3 class="doc-info-title">Calculations Guide</h3>
                  <p class="subtitle with-icon mt-2">
                    <img class="icon" width="24" alt="bulb" :src="bulbIcon"/>
                    <strong>Progress Bar Calculation:</strong> <br>
                    <span>Progress bars visually represent the status of a target based on deviation from expected progress.</span><br>
                    <span>RAG (Red, Amber, Green) Status Calculation:</span>
                  </p>
                </div>
              </v-col>
            </v-row>
            <div class="status-boxes">
              <v-row class="info-cards justify-center mb-8 mb-mb-6">
                <v-col cols="12" md="4" sm="6">
                  <div class="info-card">
                    <div class="info-card--inner">
                      <div class="card-body">
                        <div class="status-box on-track">
                          <h5 class="title">On Track</h5>
                          <p class="info">Deviation of actual progress from the expected progress is within 5%.</p>
                          <div class="progress-bar">
                            <div class="progress-status" style="width: 85%"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" md="4" sm="6">
                  <div class="info-card">
                    <div class="info-card--inner">
                      <div class="card-body">
                        <div class="status-box behind">
                          <h5 class="title">Behind</h5>
                          <p class="info">Deviation of actual progress from the expected progress is between 5-15%.</p>
                          <div class="progress-bar">
                            <div class="progress-status" style="width: 40%"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" md="4" sm="6">
                  <div class="info-card">
                    <div class="info-card--inner">
                      <div class="card-body">
                        <div class="status-box at-risk">
                          <h5 class="title">At Risk</h5>
                          <p class="info">Deviation of actual progress from the expected progress is greater than 15%.</p>
                          <div class="progress-bar">
                            <div class="progress-status" style="width: 16%"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </v-col>
              </v-row>
              <!-- Example Table -->
              <v-row class="mb-8 mb-mb-6">
                <v-col cols="12">
                  <div>
                    <h5 class="example-table-title font-weight-bold mb-2 mb-md-4">Example</h5>
                    <img class="example-progress-calculations-table" alt="progressCalculations" :src="progressCalculationsTable"/>
                  </div>
                </v-col>
              </v-row>
            </div>

            <!-- Objective Progress Calculation -->
            <div>
              <div class="doc-info__title-box mb-6 mb-md-8">
                <p class="subtitle with-icon mt-2">
                  <img class="icon weight-icon mt-1" width="24" alt="weight" :src="weightIcon"/>
                  <strong>Objective Progress Calculation Based on Key Result (KR) Weightage:</strong> <br>
                  <span>The overall progress is calculated based on the weighted contribution of each Key Result (KR):</span>
                </p>
              </div>
              <!-- Progress Table -->
              <v-row class="mb-8 mb-mb-6">
                <v-col cols="12">
                  <div class="progress-table-box">
                    <table class="progress-table">
                      <thead>
                      <tr>
                        <th>Key Result</th>
                        <th>Target</th>
                        <th>Weight</th>
                        <th>Key Result Progress</th>
                        <th>Progress Weightage</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr v-for="(kr, index) in keyResults" :key="index">
                        <td>{{ kr.name }}</td>
                        <td>${{ kr.target.toLocaleString() }}</td>
                        <td>{{ kr.weight }}%</td>
                        <td>{{ kr.progress }}%</td>
                        <td>{{ calculateProgressWeightage(kr) }}%</td>
                      </tr>
                      <tr>
                        <td>Objective</td>
                        <td>${{ totalProgressValues }}</td>
                        <td></td>
                        <td></td>
                        <td><strong>{{ totalProgressWeightage }}%</strong></td>
                      </tr>
                      </tbody>
                    </table>
                  </div>
                </v-col>
              </v-row>
            </div>

            <!-- Confidence Level: -->
            <div>
              <div class="doc-info__title-box mb-6 mb-md-8">
                <p class="subtitle with-icon mt-2">
                  <img class="icon like-icon" alt="like" :src="likeIcon"/>
                  <strong>Confidence Level:</strong> <br>
                  <span>When updating your key result, you have the option to indicate your confidence level on a scale from 1 to 10, <br> reflecting your certainty in achieving the target value.</span>
                </p>
              </div>
              <v-row class="mb-8 mb-mb-6">
                <v-col cols="12">
                  <img class="confidence-level" alt="confidenceLevel" :src="confidenceLevel"/>
                </v-col>
              </v-row>
            </div>

          </v-container>
        </section>
      </div>

<!--      <section class="doc-section bg-color-primary">-->
<!--        <v-container fluid>-->
<!--          <LogoBox :color-light="true"/>-->
<!--          <v-row class="h-100">-->
<!--            <v-col cols="12 mt-auto">-->
<!--              <h2 class="thank-you-title text-center">Thank You</h2>-->
<!--            </v-col>-->
<!--            <v-col cols="12" class="mt-auto">-->
<!--              <p class="address-info align-self-end">Office 103, Building 1, Dubai Internet City, Dubai, United Arab Emirates</p>-->
<!--            </v-col>-->
<!--          </v-row>-->
<!--        </v-container>-->
<!--      </section>-->

      <FooterSite />
    </div>
  </div>
</template>

<script>
import {eventBus} from "@/event-bus";
import { videoMixin } from '@/plugins/videoMixin';
import HeaderSite from "@/components/Header";
import FooterSite from "@/components/Footer";
import LogoBox from "@/components/LogoBox";
import VideoContent from "@/components/VideoContent";
import HelpCenterMenu from "@/components/HelpCenterMenu";

export default {
  name: "Help",
  mixins: [videoMixin],
  components: {
    HelpCenterMenu,
    VideoContent,
    HeaderSite,
    FooterSite,
    LogoBox
  },
  data() {
    return {
      siteLogo: require("@/assets/img/logo-white-text.svg"),
      siteLogoPrimary: require("@/assets/img/logo-primary-text.svg"),
      strategyPyramid: require("@/assets/img/help-page/strategy-pyramid.png"),
      bulbIcon: require("@/assets/img/icons/icon-bulb.png"),
      objectivesIcon: require("@/assets/img/icons/icon-objective.png"),
      keyResultsIcon: require("@/assets/img/icons/icon-key-results.png"),
      mirrorIcon: require("@/assets/img/icons/icon-mirror.svg"),
      linkedIcon: require("@/assets/img/icons/icon-linked.png"),
      splitIcon: require("@/assets/img/icons/icon-split.png"),
      topClick: require("@/assets/img/icons/top-click.png"),
      dependencyIcon: require("@/assets/img/icons/icon-dependency.png"),
      likeIcon: require("@/assets/img/icons/icon-like.png"),
      weightIcon: require("@/assets/img/icons/icon-weight.png"),

      pageBanner: require("@/assets/img/help-page/page-banner.png"),
      progressCalculationsTable: require("@/assets/img/help-page/progress-bar-calculations-table-example.png"),
      confidenceLevel: require("@/assets/img/help-page/confidence-level.png"),

      //Video
      helpVideo9: require("@/assets/video/help/m-9.mp4"),
      helpVideo11: require("@/assets/video/help/m-11.mp4"),
      helpVideo13: require("@/assets/video/help/m-13.mp4"),
      helpVideo16: require("@/assets/video/help/m-16.mp4"),
      helpVideo18: require("@/assets/video/help/m-18.mp4"),
      helpVideo20: require("@/assets/video/help/m-20.mp4"),
      helpVideo23: require("@/assets/video/help/m-23.mp4"),
      helpVideo25: require("@/assets/video/help/m-25.mp4"),
      helpVideo27: require("@/assets/video/help/m-27.mp4"),
      helpVideo29: require("@/assets/video/help/m-29.mp4"),
      helpVideo31: require("@/assets/video/help/m-31.mp4"),
      helpVideo35: require("@/assets/video/help/m35.mp4"),

      //menu
      showMenuTabContent: '',
      currentYear: '',

      keyResults: [
        { name: "KR1", target: 400000, weight: 40, progress: 70 },
        { name: "KR2", target: 300000, weight: 30, progress: 50 },
        { name: "KR3", target: 200000, weight: 20, progress: 80 },
        { name: "KR4", target: 100000, weight: 10, progress: 60 },
      ]
    };
  },
  beforeMount() {
    this.showMenuTabContent = this.getSelectedTab
    this.currentYear = new Date().getFullYear();
  },
  methods: {
    scrollToNextBlock() {
      const docSectionOuter = document.querySelectorAll('.doc-section--outer');
      docSectionOuter.forEach(item => {
        const nextBlock = item.querySelector('.more-info');
        if (nextBlock) {
          nextBlock.scrollIntoView({ behavior: 'smooth' });
        }
      })
    },
    scrollUpActiveSection(val){
      let selectedSection = document.body?.querySelector(`${val}`)
      if(selectedSection) {
        let pageContent = selectedSection.closest('.page-main-content')
        pageContent.style.scrollBehavior = "smooth"
        pageContent.scrollTop =  0
      }
    },
    calculateProgressWeightage(kr) {
      return Math.ceil((kr.weight / 100) * kr.progress);
    }
  },
  created() {
    eventBus.value.$on('showMenuTabContent', (value) => {
      this.showMenuTabContent = value
      setTimeout(() => {
        this.scrollUpActiveSection(`#${value}`)
      })
    });
  },
  computed: {
    getSelectedTab(){
      if (window.location.hash) {
        this.showMenuTabContent = window.location.href.split('#')[1]
      } else {
        this.showMenuTabContent = 'trainingModule'
      }
      return this.showMenuTabContent
    },
    totalProgressWeightage() {
      return this.keyResults.reduce((sum, kr) => sum + this.calculateProgressWeightage(kr), 0);
    },
    totalProgressValues() {
      return this.keyResults.reduce((sum, kr) => sum + kr.target, 0).toLocaleString();
    }
  },
  head() {
    return {
      title: "Help",
      meta: [
        {
          hid: "description",
          name: "description",
          content:
              "OKR Training module",
        },
      ],
    };
  },
};
</script>
<style lang="scss" scoped>
.main-section {
  padding-top: 64px; //header height
}
.help-page {
  display: flex;
  height: 100vh;
  overflow: hidden;
  .page-main-content {
    flex: 1;
    overflow-y: auto;
  }
}
.font-600 {
  font-weight: 600;
}
.font-500 {
  font-weight: 500;
}
.info-primary{
  color: #0442bf;
}
.bg-color-primary {
  background-color: #0442BF;
  color: #fff;
  .logo-box {
    color: #FFCA07;
  }
}
.bg-color-secondary {
  background-color: #94E0D4;
  color: #000;
  .logo-box {
    color: #0D1017;
  }
}
.bg-color-tertiary {
  background-color: #F3E3EB;
  color: #000;
  .logo-box {
    color: #1D1D1B;
  }
}
.page-banner {
  padding-top: 30px;
  padding-bottom: 30px;
  height: calc(100vh - 64px); //64px header height
  position: relative;
  .banner-img {
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    height: 100%;
    max-width: none;
    z-index: 1;
  }
  .page-title {
    font-family: 'Campton', sans-serif;
    font-size: 60px;
    line-height: 1.2;
    @media screen and (max-width: 768px){
      font-size: 48px;
    }
    .suptitle {
      font-size: 20px;
    }
    .subtitle {
      font-size: 24px;
    }
  }
  .copyright-text {
    font-family: 'Inter', sans-serif;
    font-weight: 300;
    font-size: 11px;
    color: #8FAADC;
    margin-bottom: 0;
  }
  .page-banner__content {
    position: relative;
    z-index: 2;
  }
}
.scroll-down-link {
  position: absolute;
  right: 20px;
  bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: #0442bf;
  transition: color .3s ease-in-out;
  z-index: 11;
  @media screen and (max-width: 1024px) {
    display: none;
  }
  &:hover {
    color: #002e8a;
  }
  .v-icon {
    margin-left: 8px;
  }
}
.doc-section {
  position: relative;
  padding-top: 30px;
  padding-bottom: 30px;
  min-height: calc(100vh - 64px);
  display: flex;
  p {
    font-size: 16px;
  }
  @media screen and (max-width: 992px){
    padding: 24px 0;
  }
  .address-info {
    font-family: 'Campton Book', sans-serif;
    font-size: 18px;
    margin-bottom: 0;
  }
  .thank-you-title {
    font-size: 64px;
    @media screen and (max-width: 768px){
      font-size: 48px;
    }
  }
  .doc-section-title {
    font-weight: 500;
    line-height: 1.2;
    &[data-count] {
      display: flex;
      &::before {
        font-family: 'Recoleta', sans-serif;
        content: attr(data-count);
        height: 84px;
        width: 36px;
        flex-shrink: 0;
        border-radius: 40px;
        margin-right: 32px;
        font-size: 16px;
        letter-spacing: 0.04em;
        padding: 6px;
        color: currentColor;
        border: 4px solid transparent;
        display: inline-flex;
        align-items: flex-end;
        justify-content: center;
        transform: translateY(-1em);
        @media screen and (max-width: 768px){
          width: 28px;
          height: 64px;
          font-size: 14px;
        }
      }
    }
  }
  &.bg-color-secondary {
    .doc-section-title {
      &::before {
        border-color: #0442BF;
      }
    }
  }
  &.bg-color-primary {
    .doc-section-title {
      &::before {
        border-color: #FFCA07;
      }
    }
  }
}
.doc-menu-title {
  font-size: 74px;
  color: #0D0D0D;
  @media screen and (max-width: 768px){
    font-size: 56px;
  }
}
.doc-menu-list {
  counter-reset: info-menu;
  list-style-type: none;
  font-size: 16px;
  .anchor-link {
    color: #000;
    &:hover {
      color: #0442BF;
    }
  }
  > li {
    &:not(:first-child) {
      margin-top: 16px;
    }
    .menu-title {
      .anchor-link {
        font-family: 'Campton Book', sans-serif;
        display: inline-flex;
        margin-bottom: 16px;
        &::before {
          counter-increment: info-menu;
          content: counter(info-menu, decimal-leading-zero);
          margin-right: 16px;
          font-size: 16px;
        }
      }
    }
  }
  ul {
    padding-left: 30px;
    font-family: 'Calibri', sans-serif;
    font-weight: 300;
    li {
      list-style-type: none;
      line-height: 1.4;
      &::before {
        content: '';
        display: inline-block;
        width: 0.3em;
        height: 0.3em;
        border-radius: 50%;
        background-color: currentColor;
        transform: translateY(-0.1em);
        margin-right: 16px;
      }
    }
  }
}

.doc-intro-section {
  .doc-intro-title {
    font-family: 'Campton', sans-serif;
    font-size: 60px;
    font-weight: bold;
    @media screen and (max-width: 768px){
      font-size: 42px;
    }
  }
}

.doc-info-section {
  .doc-info__title-box {
    .doc-info-title {
      font-size: 35px;
      color: #000;
      font-weight: bold;
      sub {
        font-size: 20px;
        @media screen and (max-width: 768px){
          font-size: 18px;
        }
      }
      @media screen and (max-width: 768px){
        font-size: 28px;
      }
    }
    .subtitle {
      margin-bottom: 0;
      font-size: 20px;
      @media screen and (max-width: 768px){
        font-size: 16px;
      }
      strong {
        font-size: 20px;
        color: #000;
        @media screen and (max-width: 768px){
          font-size: 18px;
        }
      }
      &.with-icon {
        position: relative;
        padding-left: 40px;
        @media screen and (max-width: 768px){
          padding-left: 30px;
        }
        .icon {
          position: absolute;
          left: 0;
          width: 20px;
          height: auto;
          &.weight-icon,
          &.like-icon {
            width: 24px;
            @media screen and (max-width: 768px){
              width: 20px;
            }
          }
          @media screen and (max-width: 768px){
            width: 16px;
          }
        }
      }
    }
  }
  .strategy-list {
    list-style-type: none;
    li {
      &:not(:last-child) {
        margin-bottom: 20px;
      }
    }
    .info {
      font-family: 'Campton', sans-serif;
      font-size: 14px;
    }
    strong {
      font-weight: bold;
      color: #385723;
    }
  }
}
.info-list {
  li {
    list-style-type: none;
    line-height: 1.2;
    position: relative;
    padding-left: 18px;
    .label {
      font-family: 'Campton', sans-serif;
      color: #0442bf;
    }
    &::before {
      content: '';
      position: absolute;
      display: inline-block;
      width: 0.25em;
      height: 0.25em;
      border-radius: 50%;
      top: 0.5em;
      left: 0;
      margin-right: 10px;
      background-color: #0442bf;
    }
  }
}
.info-card {
  height: 100%;
  display: flex;
  flex-direction: column;
  .info-card--inner {
    border-radius: 12px;
    overflow: hidden;
    height: 100%;
    display: flex;
    flex-direction: column;
  }
  .card-header,
  .card-body,
  .card-footer {
    padding: 12px 16px;
  }
  .card-header {
    background-color: #B7DFBD;
    &.bg-header-primary {
      background-color: #E6EEFF;
    }
    &.bg-header-secondary {
      background-color: #FFFAE5;
    }
    .info-primary {
      font-family: 'Recoleta', sans-serif;
      margin-bottom: 0;
    }
  }
  .card-body {
    font-weight: 300;
    background-color: #fff;
    flex: 1;
    font-size: 14px;
    display: flex;
    flex-direction: column;
    .top-intro {
      color: #2F4B46;
    }
    p {
      font-size: 14px;
    }
    strong {
      font-weight: bold;
    }
    ol, ul {
      padding-left: 16px;
      li {
        line-height: 1.3;
        &:not(:last-child) {
          margin-bottom: 12px;
        }
      }
    }
  }
  &.relationship-card {
    .card-title {
      font-family: 'Campton', sans-serif;
      font-size: 14px;
      &.with-icon {
        .icon {
          order: 1;
          margin-left: auto;
        }
      }
    }
  }
  .card-title {
    font-family: 'Campton', sans-serif;
    font-size: 14px;
    &.with-icon {
      position: relative;
      display: flex;
      align-items: center;
      gap: 8px;
      .icon {
        max-height: 30px;
        height: 100%;
        flex-shrink: 0;
      }
    }
  }
  .card-footer {
    background-color: #fff;
    padding-top: 0;
    .card-footer--inner {
      padding-top: 12px;
      border-top: 1px solid #bcbcbc;
    }
  }
  .info-examples {
    min-height: 120px;
    display: flex;
    gap: 12px;
    flex-direction: column;
    justify-content: center;
    .label {
      font-size: 10px;
      color: #7F7F7F;
      margin-bottom: 4px;
      display: block;
    }
    .example-description {
      font-size: 11x;
      color: #000;
    }
    p {
      font-size: 12px;
      margin-bottom: 0;
    }
  }
}
.info-card-steps {
  .info-card--inner {
    border-radius: 8px;
  }
  .info-card {
    &:not(.info-card-right) {
      .card-body {
        padding-right: 50px;
        @media screen and (max-width: 601px){
          padding-right: 0;
        }
      }
    }
  }
  .info-card-center {
    margin-left: -50px;
    @media screen and (max-width: 601px){
      margin-left: 0;
    }
  }
  .info-card-center,
  .info-card-right {
    margin-top: -80px;
    width: 100%;
    @media screen and (max-width: 601px){
      margin-top: 0;
    }
  }
  .info-card-right {
    margin-left: -100px;
    @media screen and (max-width: 601px){
      margin-left: 0;
    }
  }
}
#strategyPyramid {
  .doc-info-title {
    font-size: 40px;
    position: absolute;
    right: 0;
    top: 20px;
    max-width: 20%;
    @media screen and (max-width: 768px){
      max-width: none;
      font-size: 32px;
      position: relative;
      top: auto;
      right: auto;
    }
  }
}
.update-key-steps {
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  .update-key-step {
    position: relative;
    display: flex;
    align-items: center;
    flex: 0 0 70%;
    max-width: 70%;
    width: 100%;
    flex: 1;
    &.selected {
      .desc {
        background-color: #6EACA2;
        color: #fff;
      }
    }
    &:not(:last-child) {
      margin-bottom: 10px;
      &::after {
        content: '';
        position: absolute;
        background-image: url('@/assets/img/icons/step-arrow.png');
        background-position: center;
        background-size: cover;
        width: 42px;
        height: 42px;
        top: calc(100% - 20px);
        right: 6px;
        z-index: 1;
      }
    }
    .title {
      font-size: 14px;
      font-weight: normal;
      white-space: nowrap;
      flex-shrink: 0;
      margin-right: 12px;
    }
    .desc {
      padding: 6px 50px 6px 6px;
      background-color: #B7E0BE;
      min-height: 40px;
      width: 100%;
      height: 100%;
      border-radius: 6px;
      color: #262626;
      display: flex;
      align-items: center;
      p {
        font-size: 14px;
        margin-bottom: 0;
      }
    }
  }
}

/*Calculations Guide*/
/*Status boxes*/
.status-boxes {
  font-family: 'Campton', sans-serif;
  max-width: 90%;
  margin-left: auto;
  margin-right: auto;
}
.status-box {
  display: flex;
  flex-direction: column;
  height: 100%;
  .title {
    font-family: 'Campton', sans-serif;
    font-weight: bold;
    font-size: 18px;
    @media screen and (max-width: 768px){
      font-size: 16px;
    }
  }
  .progress-bar {
    position: relative;
    height: 30px;
    border-radius: 4px;
    overflow: hidden;
    margin-top: auto;
    @media screen and (max-width: 768px){
      height: 28px;
    }
    .progress-status {
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      height: 100%;
    }
  }

  &.on-track {
    .title {
      color: #1FAC5E;
    }
    .progress-bar {
      background-color: #78CD9B;
    }
    .progress-status  {
      background-color: #1FAC5E;
    }
  }
  &.behind {
    .title {
      color: #ED7B24;
    }
    .progress-bar {
      background-color: #F3AF7F;
    }
    .progress-status  {
      background-color: #ED7B24;
    }
  }
  &.at-risk {
    .title {
      color: #D74B4C;
    }
    .progress-bar {
      background-color: #E69394;
    }
    .progress-status  {
      background-color: #D74B4C;
    }
  }
}
.example-table-title {
  font-family: 'Campton', sans-serif;
  font-size: 16px;
  @media screen and (max-width: 768px) {
    font-size: 14px;
  }
}
/*Progress Table*/
.progress-table-box {
  table {
    width: 100%;
    border-collapse: collapse;
  }

  th, td {
    border: 1px solid #ddd;
    padding: 10px;
    text-align: left;
    @media screen and (max-width: 768px) {
      font-size: 14px;
      padding: 6px;
    }
    @media screen and (max-width: 575px) {
      font-size: 12px;
    }
  }

  th {
    background: #f5f5f5;
    font-weight: bold;
  }

  td {
    background: #fff;
  }
}

</style>
